import { useState, useCallback, useEffect, useRef } from "react";
import Sidebar, { type Project } from "./components/Sidebar";
import ColumnLayout, { type ColumnRef } from "./components/ColumnLayout";
import TopBar from "./components/TopBar";
import CommandPalette from "./components/CommandPalette";
import LoadingScreen from "./components/LoadingScreen";
import LoginPage from "./components/LoginPage";
import InvitePage from "./components/InvitePage";
import BootstrapPage from "./components/BootstrapPage";
import AdminUsersPage from "./components/AdminUsersPage";
import AdminUsagePage from "./components/AdminUsagePage";
import AdminErrorsPage from "./components/AdminErrorsPage";
import AdminQaPage from "./components/AdminQaPage";
import BuildsPage from "./components/BuildsPage";
import QaPage from "./components/QaPage";
import QaOverview from "./components/QaOverview";
import CodeReviewPage from "./components/CodeReviewPage";
import ChangePasswordPage from "./components/ChangePasswordPage";
import { useMe } from "./hooks/useMe";
import { HttpError } from "./api/errors";
import { needsBootstrap } from "./api/auth";
import { parseRoute, routeToPath, type AppView, type QaTab } from "./lib/route";
import { shareTestRun } from "./api/qaShare";
import QaRunPage from "./components/QaRunPage";

// Only sync the URL for real http(s) documents. In test environments the
// document can be about:blank, where history.* throws a SecurityError.
function canRoute(): boolean {
  return typeof window !== "undefined" && /^https?:$/.test(window.location.protocol);
}

export default function App() {
  const me = useMe();
  // Restore the view + selected project straight from the URL so a hard reload
  // lands back on the same page (e.g. Builds) instead of the chat dashboard.
  const initialRoute = parseRoute(canRoute() ? window.location.pathname : "/", canRoute() ? window.location.search : "");
  const [selectedId, setSelectedId] = useState<string | null>(initialRoute.projectId);
  const [runId, setRunId] = useState<string | null>(initialRoute.runId ?? null);
  const [qaTab, setQaTab] = useState<QaTab>(initialRoute.tab ?? "runs");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [view, setView] = useState<AppView>(initialRoute.view);
  const [adminTab, setAdminTab] = useState<"users" | "usage" | "errors" | "qa">("users");
  const [bootstrap, setBootstrap] = useState<boolean | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const lastViewRef = useRef<AppView>(initialRoute.view);
  const lastRunRef = useRef<string | null>(runId);
  const lastTabRef = useRef<QaTab>(qaTab);
  // A share link (CI posts one on the PR) reads its test run without login,
  // and keeps its token in the URL so a reload still opens it.
  const [shared] = useState(() => {
    if (!initialRoute.share || !initialRoute.runId || !initialRoute.projectId) return null;
    shareTestRun(initialRoute.runId, initialRoute.share);
    return { project: initialRoute.projectId, runId: initialRoute.runId, token: initialRoute.share };
  });

  // Keep the URL in sync with the current view + project. A section change
  // (chat <-> builds <-> admin) pushes a history entry so the browser Back
  // button returns to the previous section; switching projects within the
  // same section replaces, to avoid flooding history on every project click.
  useEffect(() => {
    if (!canRoute()) return;
    const onShared = shared && view === "qa" && selectedId === shared.project && runId === shared.runId;
    const path = routeToPath(view, selectedId, runId, qaTab) + (onShared ? `?share=${encodeURIComponent(shared.token)}` : "");
    if (path !== window.location.pathname + (onShared ? window.location.search : "")) {
      try {
        // Opening or leaving a test run, or switching QA tabs, is a page change too.
        if (view !== lastViewRef.current || runId !== lastRunRef.current || qaTab !== lastTabRef.current) window.history.pushState(null, "", path);
        else window.history.replaceState(null, "", path);
      } catch { /* ignore environments that forbid history changes */ }
    }
    lastViewRef.current = view;
    lastRunRef.current = runId;
    lastTabRef.current = qaTab;
  }, [view, selectedId, runId, qaTab, shared]);

  // Browser Back/Forward: re-derive the view + project from the new URL.
  useEffect(() => {
    const onPop = () => {
      const r = parseRoute(canRoute() ? window.location.pathname : "/", canRoute() ? window.location.search : "");
      lastViewRef.current = r.view; // keep the sync effect from re-pushing
      lastRunRef.current = r.runId ?? null;
      lastTabRef.current = r.tab ?? "runs";
      setView(r.view);
      setQaTab(r.tab ?? "runs");
      setSelectedId(r.projectId);
      setRunId(r.runId ?? null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // A non-admin who lands on /admin (hand-typed or stale link) falls back to chat.
  useEffect(() => {
    if (view === "admin" && me.data && me.data.role !== "admin") setView("chat");
  }, [view, me.data]);

  // Multi-column state.
  const [columns, setColumns] = useState<ColumnRef[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Mirror focusedIndex into a ref so the openConversation updater reads the
  // live value instead of the one captured in its callback closure.
  const focusedIndexRef = useRef(focusedIndex);
  useEffect(() => {
    focusedIndexRef.current = focusedIndex;
  }, [focusedIndex]);

  // Open a conversation: replace focused column (plain) or append (Shift).
  const openConversation = useCallback((ref: ColumnRef, additive: boolean) => {
    setColumns((prev) => {
      const existing = prev.findIndex(
        (c) => c.project === ref.project && c.conversationId === ref.conversationId,
      );
      if (existing >= 0) {
        setFocusedIndex(existing);
        return prev;
      }
      if (additive || prev.length === 0) {
        setFocusedIndex(prev.length);
        return [...prev, ref];
      }
      const next = [...prev];
      next[focusedIndexRef.current] = ref;
      return next;
    });
  }, []);

  // Open a conversation by id as a focused NEW column (additive). Used by the
  // Issues "Continue in my chat" takeover, which copies a conversation and then
  // wants it surfaced as its own column.
  const handleOpenConversation = useCallback(
    (conversationId: number, branch: string | null, worktreeDir?: string | null) => {
      if (!selectedId) return;
      openConversation({ project: selectedId, conversationId, branch, worktreeDir }, true);
    },
    [selectedId, openConversation],
  );

  const closeColumn = useCallback((index: number) => {
    setColumns((prev) => prev.filter((_, i) => i !== index));
    setFocusedIndex((f) => Math.max(0, f - (index <= f ? 1 : 0)));
  }, []);

  // ⌘K / Ctrl+K toggles the command palette.
  // ⌘1..9 / Ctrl+1..9 focus a column.
  // ⌘W / Ctrl+W closes the focused column.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdkOpen((o) => !o);
      }
      if ((e.metaKey || e.ctrlKey) && e.key >= "1" && e.key <= "9") {
        const idx = Number(e.key) - 1;
        setFocusedIndex((f) => (idx < columns.length ? idx : f));
        e.preventDefault();
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "w") {
        e.preventDefault();
        closeColumn(focusedIndex);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [columns.length, focusedIndex, closeColumn]);

  // The Deploy popover dispatches this to send the user to the Builds page
  // (no-helper / iPad download path).
  useEffect(() => {
    // Only honor the event when a project is selected, so view never latches to
    // "builds" with no project (which would strand the layout with no way back).
    const open = () => { if (selectedId) setView("builds"); };
    window.addEventListener("thunderay-ai:open-builds", open);
    return () => window.removeEventListener("thunderay-ai:open-builds", open);
  }, [selectedId]);

  // The QA tests tab in a chat column dispatches this to open the QA tests
  // page for that column's project. Carrying the project in the event keeps it
  // correct even when several columns from different projects are open.
  useEffect(() => {
    const open = (e: Event) => {
      const project = (e as CustomEvent<{ project?: string }>).detail?.project;
      if (project) setSelectedId(project);
      if (project || selectedId) { setRunId(null); setQaTab("runs"); setView("qa"); }
    };
    window.addEventListener("thunderay-ai:open-qa", open as EventListener);
    return () => window.removeEventListener("thunderay-ai:open-qa", open as EventListener);
  }, [selectedId]);

  // The Code Review tab in a chat column dispatches this to open the standalone
  // Code Review queue page for that column's project (same pattern as open-qa).
  useEffect(() => {
    const open = (e: Event) => {
      const project = (e as CustomEvent<{ project?: string }>).detail?.project;
      if (project) setSelectedId(project);
      if (project || selectedId) setView("review");
    };
    window.addEventListener("thunderay-ai:open-review", open as EventListener);
    return () => window.removeEventListener("thunderay-ai:open-review", open as EventListener);
  }, [selectedId]);

  // When unauthenticated, ask the server if bootstrap is needed.
  useEffect(() => {
    const unauth = me.error instanceof HttpError && me.error.status === 401;
    if (!unauth) {
      setBootstrap(null);
      return;
    }
    let cancelled = false;
    needsBootstrap()
      .then((b) => { if (!cancelled) setBootstrap(b); })
      .catch(() => { if (!cancelled) setBootstrap(false); });
    return () => { cancelled = true; };
  }, [me.error]);

  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
    setSidebarOpen(false);
  }, []);

  const handleProjectsLoaded = useCallback((loaded: Project[]) => {
    setProjects(loaded);
    setSelectedId((cur) => cur ?? (loaded.length > 0 ? loaded[0].id : null));
  }, []);

  // Human-facing name for a registry id. The id is the database key and can
  // differ from the product name (registry id `multipack` is the Ultimate
  // Survival Add-On), so headers must never print the id. `/me` carries the
  // list on every route, including hard loads of /builds, /qa and /review
  // where the sidebar (and therefore `projects`) is never mounted.
  const meProjects = me.data?.projects;
  const projectName = useCallback(
    (id: string): string =>
      meProjects?.find((p) => p.id === id)?.name ??
      projects.find((p) => p.id === id)?.name ??
      id,
    [meProjects, projects],
  );

  // The one route that must render before /me resolves: whoever follows an
  // invite link has no session yet, and the token in the URL fragment is the
  // only credential they hold. Letting the normal unauthenticated path run
  // first would drop them on LoginPage with nothing to sign in with.
  if (view === "invite") return <InvitePage />;

  if (me.loading) return <LoadingScreen />;

  const unauthorized = me.error instanceof HttpError && me.error.status === 401;
  // A shared run for someone signed out, or without the project: that run only, read-only.
  if (shared && view === "qa" && selectedId === shared.project && runId === shared.runId && !me.data?.projects?.some((p) => p.id === shared.project)) {
    return (
      <div className="qa-page qa-page-wide">
        <QaRunPage project={shared.project} projectName={shared.project} runId={shared.runId} onBack={() => {}} readOnly />
      </div>
    );
  }
  if (unauthorized || !me.data) {
    if (bootstrap === null) return <LoadingScreen />;
    return bootstrap ? <BootstrapPage /> : <LoginPage />;
  }

  if (me.data.requirePasswordChange) {
    return <ChangePasswordPage />;
  }

  if (view === "admin" && me.data.role === "admin") {
    const openQa = () => setAdminTab("qa");
    if (adminTab === "usage") {
      return <AdminUsagePage onClose={() => setView("chat")} onOpenUsers={() => setAdminTab("users")} onOpenErrors={() => setAdminTab("errors")} onOpenQa={openQa} />;
    }
    if (adminTab === "errors") {
      return <AdminErrorsPage onClose={() => setView("chat")} onOpenUsers={() => setAdminTab("users")} onOpenUsage={() => setAdminTab("usage")} onOpenQa={openQa} />;
    }
    if (adminTab === "qa") {
      return <AdminQaPage onClose={() => setView("chat")} onOpenUsers={() => setAdminTab("users")} onOpenUsage={() => setAdminTab("usage")} onOpenErrors={() => setAdminTab("errors")} />;
    }
    return <AdminUsersPage onClose={() => setView("chat")} onOpenUsage={() => setAdminTab("usage")} onOpenErrors={() => setAdminTab("errors")} onOpenQa={openQa} />;
  }

  if (view === "builds" && selectedId) {
    return <BuildsPage project={selectedId} projectName={projectName(selectedId)} onClose={() => setView("chat")} />;
  }

  if (view === "qa" && selectedId) {
    return (
      <QaPage
        project={selectedId}
        projectName={projectName(selectedId)}
        runId={runId}
        onOpenRun={setRunId}
        tab={qaTab}
        onTab={setQaTab}
        onClose={() => (runId ? setRunId(null) : setView("qaHome"))}
      />
    );
  }

  if (view === "qaHome" || view === "qa") {
    return (
      <QaOverview
        projects={me.data.projects}
        onOpenProject={(id, tab = "runs") => { setSelectedId(id); setRunId(null); setQaTab(tab); setView("qa"); }}
        onClose={() => setView("chat")}
      />
    );
  }

  if (view === "review" && selectedId) {
    return <CodeReviewPage project={selectedId} projectName={projectName(selectedId)} onClose={() => setView("chat")} onSelectProject={setSelectedId} />;
  }

  const showEmptyMember =
    me.data.role === "member" && me.data.projects.length === 0;

  return (
    <div className="layout">
      <TopBar
        me={me.data}
        loading={me.loading}
        error={me.error}
        onToggleSidebar={() => setSidebarOpen((o) => !o)}
        onManageUsers={() => { setAdminTab("users"); setView("admin"); }}
        onOpenQa={() => setView("qaHome")}
        onOpenCmdk={projects.length > 0 ? () => setCmdkOpen(true) : undefined}
      />
      <aside className={`sidebar${sidebarOpen ? " open" : ""}`}>
        <h2 className="sidebar-title">Projects</h2>
        {showEmptyMember ? (
          <p className="empty-projects">
            You do not have access to any projects yet. Ask an admin to assign
            you a project in the Users panel.
          </p>
        ) : (
          <Sidebar
            selectedId={selectedId}
            onSelect={handleSelect}
            onProjectsLoaded={handleProjectsLoaded}
            onOpenConversation={openConversation}
          />
        )}
      </aside>
      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <main className="chat">
        <ColumnLayout
          columns={columns}
          focusedIndex={focusedIndex}
          projectName={projectName}
          onFocus={setFocusedIndex}
          onClose={closeColumn}
          onOpenConversation={handleOpenConversation}
        />
      </main>
      <CommandPalette
        open={cmdkOpen}
        onClose={() => setCmdkOpen(false)}
        projects={projects}
        onSelectProject={(id) => {
          handleSelect(id);
          setView("chat");
        }}
      />
    </div>
  );
}
